import { Suspense, lazy, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyBrandTheme, safeBrandLogoUrl } from '../lib/brand';
import { track } from '../lib/analytics';
import { useResolvedScheme } from '../lib/theme';
import { COPYRIGHT } from '../components/shell/legal';
import { InlineGroupField } from '../components/InlineGroupField';

const FormEnginePortable = lazy(() =>
  import('@alliance-strategies/form-engine-portable').then((m: any) => ({
    default: (m.FormEnginePortable ?? m.default) as React.ComponentType<Record<string, unknown>>,
  })),
);

interface LookupData {
  submission: {
    id: string;
    refereeName: string;
    referenceType: string;
    applicantName?: string;
    programName?: string | null;
  };
  fieldsConfig: { layout: { type: string; fields: unknown[] } } | null;
  brand: {
    name: string;
    logoUrl: string | null;
    colors: Record<string, string> | null;
  } | null;
}

type TerminalState = 'invalid' | 'expired' | 'revoked' | 'completed' | 'submitted';

/**
 * Public referee reference form (/reference/respond?token=…) — the link a
 * referee receives by email. No portal account, no login: the one-time token
 * is the whole authorization, so every non-open token state renders a polite
 * terminal page instead of the form (the same contract on GET and POST covers
 * the replaced-mid-form race). Questions arrive as a form-engine fieldsConfig
 * adapted from the Globus reference template.
 */
export default function RefereeResponsePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [data, setData] = useState<LookupData>();
  const [terminal, setTerminal] = useState<TerminalState>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const scheme = useResolvedScheme();

  useEffect(() => {
    if (!token) {
      setTerminal('invalid');
      return;
    }
    publicApi<LookupData>(`/references/lookup?token=${encodeURIComponent(token)}`)
      .then((d) => {
        setData(d);
        applyBrandTheme(d.brand);
        track('reference_form_opened', { referenceType: d.submission.referenceType });
      })
      .catch((e: PublicApiError) => {
        setTerminal(terminalFromError(e));
        track('reference_form_blocked', { state: terminalFromError(e) });
      });
  }, [token]);

  // FormEnginePortable always calls onSave before onComplete; this form has
  // no draft-save step distinct from the final submission, so onSave is a
  // deliberate no-op — the real POST happens once, in onComplete (submit).
  async function noopSave() {}

  async function submit(responses: Record<string, unknown>) {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await publicApi('/references/respond', {
        method: 'POST',
        body: JSON.stringify({ token, responses }),
      });
      track('reference_form_submitted', {});
      setTerminal('submitted');
    } catch (e) {
      const err = e as PublicApiError;
      const state = terminalFromError(err, true);
      if (state) {
        setTerminal(state);
      } else if (err?.status === 429) {
        // Rate-limited: distinct from a network failure — retrying immediately
        // will just 429 again, so tell the referee to slow down instead of
        // implying a connectivity problem.
        setError("You're submitting a bit fast — please wait a moment and try again.");
      } else if (typeof err?.status === 'number' && err.status >= 500) {
        // A real structured 5xx from the server — not a network failure, so
        // "check your connection" would be misleading.
        setError("Something went wrong on our end. Your answers are still on this page — please try again shortly.");
      } else {
        // No `.status` at all (or an unrecognised status): a genuine network/
        // timeout failure is the only case where this framing is honest.
        setError(
          "We couldn't submit the reference. Your answers are still on this page — please check your connection and try again.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  const logo = safeBrandLogoUrl(data?.brand?.logoUrl);
  return (
    <div className="publicpage">
      <header className="publicpage-brand">
        {logo ? (
          <img className="logo" src={logo} alt={data?.brand?.name ?? ''} />
        ) : (
          <span className="logo" aria-hidden>
            AA
          </span>
        )}
        <span>{data?.brand?.name ?? 'Alliance Abroad'}</span>
      </header>
      <main>
        {terminal ? (
          <TerminalCard state={terminal} />
        ) : !data ? (
          <div className="card formcard skeleton">Loading reference form…</div>
        ) : !data.fieldsConfig?.layout?.fields?.length ? (
          // The submission resolved but carries no renderable questionnaire
          // (no template configured for this reference type). Never show an
          // empty form with a submit button — surface it as a config problem.
          <div className="card formcard referee-terminal" role="alert">
            <h2>This reference form isn't ready yet</h2>
            <p>
              We couldn't load the questionnaire for this reference request. Please try again shortly — if it keeps
              happening, let the applicant know so the program team can look into it.
            </p>
          </div>
        ) : (
          <div className="card formcard referee-form">
            <header className="formhead">
              <h2>Reference Request{data.submission.applicantName ? ` for ${data.submission.applicantName}` : ''}</h2>
              <p className="muted">
                {[data.brand?.name, data.submission.programName].filter(Boolean).join(' • ')}
              </p>
            </header>
            {error && (
              <div className="globus-banner err" role="alert">
                {error}
              </div>
            )}
            <Suspense fallback={<p>Loading form…</p>}>
              <FormEnginePortable
                theme={scheme}
                stepId="reference_response"
                hideTitle
                // The e-signature placeholder personalizes from
                // applicationData first/last name — the signer here is the
                // REFEREE, so hand it their name split on the first space
                // (without this, engines < 2.23.1 rendered the literal
                // "Type your name (undefined undefined)").
                applicationData={{
                  firstName: data.submission.refereeName.split(' ')[0] ?? '',
                  lastName: data.submission.refereeName.split(' ').slice(1).join(' '),
                }}
                stepConfig={{
                  stepKey: 'reference_response',
                  name: 'Reference Form',
                  fieldsConfig: data.fieldsConfig,
                }}
                stepProgress={{ status: 'not_started', stepData: {}, step_data: {} }}
                initialFormData={{}}
                isLastStep
                // The engine's doSaveAndComplete always calls onSave THEN
                // onComplete for a step with data (see form-engine-portable's
                // doSaveAndComplete). This is a one-shot referee response,
                // not a step with separate draft/complete semantics, so
                // onSave must NOT hit the network — wiring both to `submit`
                // POSTs /references/respond twice per click (the second
                // always 409s ALREADY_COMPLETED since the first already
                // completed the row).
                onSave={noopSave}
                onComplete={submit}
                onError={(e: Error, ctx: string) => {
                  if (ctx === 'save' || ctx === 'complete') {
                    setError(
                      "We couldn't submit the reference. Your answers are still on this page — please try again.",
                    );
                  }
                }}
                onUploadDocument={(file: File | Blob, ctx: { fieldKey: string }) =>
                  uploadReferenceMedia(token, ctx.fieldKey, file)
                }
                resolveDocumentUrl={(url: string) => resolveReferenceMediaUrl(url, token)}
                fieldComponents={{ inline_group: InlineGroupField }}
              />
            </Suspense>
          </div>
        )}
      </main>
      <footer>{COPYRIGHT}</footer>
    </div>
  );
}

const TERMINAL_COPY: Record<TerminalState, { title: string; body: string }> = {
  submitted: {
    title: 'Thank you — reference submitted',
    body: 'Your reference has been received and will be reviewed by the program team. You can close this page.',
  },
  completed: {
    title: 'This reference was already submitted',
    body: 'A response for this reference request is already on record — nothing more is needed from you. Thank you!',
  },
  expired: {
    title: 'This reference link has expired',
    body: 'The request has passed its response window. If you still wish to provide a reference, please ask the applicant to send a new request.',
  },
  revoked: {
    title: 'This reference request is no longer active',
    body: 'The applicant has updated their referee details, so this link has been retired. No action is needed from you.',
  },
  invalid: {
    title: 'This reference link is not valid',
    body: 'The link may be incomplete — please open the most recent reference request email and use the button or full link inside it.',
  },
};

function TerminalCard({ state }: { state: TerminalState }) {
  const copy = TERMINAL_COPY[state];
  return (
    <div className="card formcard referee-terminal" role="status">
      <h2>{copy.title}</h2>
      <p>{copy.body}</p>
    </div>
  );
}

/** Structured error from the public API — keeps Globus's {error, state} body. */
class PublicApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(typeof body?.message === 'string' ? body.message : `request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

/**
 * Public fetch: no bearer token, no 401→sign-in redirect (the referee has no
 * session to expire), and errors keep the structured body so terminal states
 * are machine-readable.
 */
async function publicApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new PublicApiError(res.status, body);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

/**
 * Referee stamp/signature upload — plain multipart fetch (no bearer token,
 * same unauthenticated model as publicApi above; a FormData body needs the
 * browser's own Content-Type boundary, so this can't reuse publicApi's
 * always-JSON headers). Returns the shape onUploadDocument's contract
 * expects; `url` here is already BFF-relative (the controller rewrites the
 * Hub's own proxy path) — resolveReferenceMediaUrl below appends the token
 * when actually fetching bytes for a preview.
 */
async function uploadReferenceMedia(
  token: string,
  fieldKey: string,
  file: File | Blob,
): Promise<{ url: string; filename?: string; mediaId: string }> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const form = new FormData();
  form.append('token', token);
  form.append('fieldKey', fieldKey);
  form.append('file', file, file instanceof File ? file.name : 'upload.png');
  const res = await fetch(`${base}/api/v1/references/media`, { method: 'POST', body: form });
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new PublicApiError(res.status, body);
  }
  const json = await res.json();
  return json.data;
}

/**
 * Resolves a stored stamp/signature reference (its BFF-relative `url`) to an
 * authenticated (token-bearing) blob: object URL — the contract
 * FileUploadRenderer/ElectronicSignatureFieldRenderer's preview expects.
 */
async function resolveReferenceMediaUrl(url: string, token: string): Promise<{ url: string; type?: string }> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const withToken = `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  const res = await fetch(`${base}${withToken}`);
  if (!res.ok) throw new Error(`failed to load reference media (${res.status})`);
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

function terminalFromError(e: PublicApiError, submitPhase = false): TerminalState | undefined {
  const state = e?.body?.state ?? e?.body?.error?.state;
  // Defensive: a retry (double-click, flaky connection, or a re-invoked
  // submit) that lands after the reference was already completed should
  // read as success, not a generic retryable error — regardless of whether
  // this particular 409 body carries a `state` field.
  if (state === 'completed' || e?.body?.error === 'ALREADY_COMPLETED') return 'completed';
  if (state === 'expired') return 'expired';
  if (state === 'revoked') return 'revoked';
  if (e?.status === 404) return 'invalid';
  if (e?.status === 410) return 'expired';
  // GET failures with no recognisable state still block the form; POST
  // failures fall through to a retryable inline error instead.
  return submitPhase ? undefined : 'invalid';
}
